import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import subscriptionService from '../../../services/subscriptionService';

const WhatWeOfferSection = ({ business, businessId, onUpgrade }) => {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pricingTiers, setPricingTiers] = useState([]);

  // Load subscription data
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        setIsLoading(true);
        const subscription = await subscriptionService?.getBusinessSubscription(businessId || business?.id);
        const tiers = await subscriptionService?.getPricingTiers();
        
        setSubscriptionData(subscription);
        setPricingTiers(tiers);
      } catch (error) {
        console.error('Error loading subscription data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (businessId || business?.id) {
      loadSubscriptionData();
    }
  }, [businessId, business?.id]);

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded?.has(index)) {
      newExpanded?.delete(index);
    } else {
      newExpanded?.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const handleAddService = () => {
    const isAuthenticated = localStorage.getItem('authToken');
    
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          returnTo: '/add-service',
          message: 'Please log in to add services to your business'
        }
      });
      return;
    }

    if (!subscriptionData?.isPremium) {
      // Trigger upgrade flow
      handleUpgradeClick();
      return;
    }

    if (!subscriptionData?.canAddServices) {
      // Show upgrade to premium plus or limit reached message
      handleUpgradeClick('limit_reached');
      return;
    }

    // Navigate to add service form with proper authorization
    navigate('/add-service', {
      state: {
        fromWhatWeOffer: true,
        businessId: businessId || business?.id
      }
    });
  };

  const handleUpgradeClick = (reason = 'upgrade') => {
    if (onUpgrade) {
      onUpgrade(reason, subscriptionData);
    } else {
      // Show upgrade modal or redirect to upgrade flow
      handleShowUpgradeModal(reason);
    }
  };

  const handleShowUpgradeModal = async (reason) => {
    const premiumTier = pricingTiers?.find(tier => tier?.tier_type === 'premium');
    const premiumPlusTier = pricingTiers?.find(tier => tier?.tier_type === 'premium_plus');

    const message = reason === 'limit_reached' ? `You've reached your service limit (${subscriptionData?.servicesLimit}). Upgrade to Premium Plus for £4.99 to add 4 more services.`
      : `Unlock premium service management for £9.99/year. Add up to 5 detailed services to your business profile.`;

    // For now, simulate payment and upgrade
    if (window.confirm(`${message}\n\nWould you like to upgrade now?`)) {
      try {
        const targetTier = reason === 'limit_reached' ? 'premium_plus' : 'premium';
        await subscriptionService?.upgradeSubscription(
          businessId || business?.id, 
          targetTier,
          `DEMO_PAYMENT_${Date.now()}`
        );
        
        // Reload subscription data
        const updatedSubscription = await subscriptionService?.getBusinessSubscription(businessId || business?.id);
        setSubscriptionData(updatedSubscription);
        
        // Store premium status in localStorage for form access
        localStorage.setItem('isPremium', 'true');
        
        alert('Subscription upgraded successfully!');
      } catch (error) {
        console.error('Upgrade error:', error);
        alert('Upgrade failed. Please try again.');
      }
    }
  };

  const handleManageServices = () => {
    const isAuthenticated = localStorage.getItem('authToken');
    
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          returnTo: '/user-profile',
          message: 'Please log in to manage your services'
        }
      });
      return;
    }

    navigate('/user-profile', { state: { activeTab: 'services' } });
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  const isPremium = subscriptionData?.isPremium;
  const servicesLimit = subscriptionData?.servicesLimit || 0;
  const servicesUsed = subscriptionData?.servicesUsed || 0;
  const canAddServices = subscriptionData?.canAddServices;

  if (!business?.whatWeOffer || business?.whatWeOffer?.length === 0) {
    return (
      <div className="bg-card rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            What We Offer
          </h2>
          {isPremium ? (
            <div className="flex items-center space-x-2">
              <Icon name="Crown" size={16} className="text-warning" />
              <span className="text-sm text-muted-foreground font-body">
                Premium Active ({servicesUsed}/{servicesLimit} services)
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Icon name="Crown" size={16} className="text-warning" />
              <span className="text-sm text-muted-foreground font-body">Premium Feature</span>
            </div>
          )}
        </div>

        {/* Empty state */}
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-heading font-medium text-foreground mb-2">
            No Services Listed Yet
          </h3>
          <p className="text-muted-foreground font-body mb-6 max-w-md mx-auto">
            {isPremium 
              ? canAddServices
                ? "Start adding services to showcase what you offer to potential clients."
                : `You've used all ${servicesLimit} service slots. Upgrade to Premium Plus for 4 more slots.`
              : "Upgrade to premium to add detailed service information, pricing, and booking options."
            }
          </p>
          
          {isPremium ? (
            canAddServices ? (
              <Button
                variant="default"
                onClick={handleAddService}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}
              >
                Add Your First Service
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => handleUpgradeClick('limit_reached')}
                iconName="Crown"
                iconPosition="left"
                iconSize={16}
              >
                Upgrade for £4.99/year
              </Button>
            )
          ) : (
            <Button
              variant="default"
              onClick={handleUpgradeClick}
              iconName="Crown"
              iconPosition="left"
              iconSize={16}
            >
              Upgrade for £9.99/year
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          What We Offer
        </h2>
        <div className="flex items-center space-x-3">
          {isPremium ? (
            <>
              <div className="flex items-center space-x-2">
                <Icon name="Crown" size={16} className="text-warning" />
                <span className="text-sm text-muted-foreground font-body">
                  Premium Active ({servicesUsed}/{servicesLimit} services)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleManageServices}
                  iconName="Settings"
                  iconPosition="left"
                  iconSize={14}
                >
                  Manage
                </Button>
                {canAddServices ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddService}
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Add Service
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUpgradeClick('limit_reached')}
                    iconName="Crown"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Upgrade +4 Services
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center space-x-2">
              <Icon name="Crown" size={16} className="text-warning" />
              <span className="text-sm text-muted-foreground font-body">Premium Feature</span>
            </div>
          )}
        </div>
      </div>

      {!isPremium ? (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-heading font-medium text-foreground mb-2">
            Unlock Detailed Service Information
          </h3>
          <p className="text-muted-foreground font-body mb-6 max-w-md mx-auto">
            Get access to comprehensive service details, pricing, and availability with our premium subscription.
          </p>
          <Button
            variant="default"
            onClick={handleUpgradeClick}
            iconName="Crown"
            iconPosition="left"
            iconSize={16}
          >
            Upgrade for £9.99/year
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {business?.whatWeOffer?.map((service, index) => {
            const isExpanded = expandedItems?.has(index);
            const shouldTruncate = service?.description?.length > 150;
            const displayDescription = shouldTruncate && !isExpanded
              ? service?.description?.substring(0, 150) + '...'
              : service?.description;

            return (
              <div
                key={index}
                className="border border-border rounded-lg p-4 hover:shadow-organic transition-organic"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={service?.icon || 'Star'} size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-medium text-foreground">
                        {service?.name}
                      </h3>
                      {service?.duration && (
                        <p className="text-sm text-muted-foreground font-body">
                          {service?.duration}
                        </p>
                      )}
                    </div>
                  </div>
                  {service?.price && (
                    <span className="text-primary font-body font-semibold">
                      £{service?.price}
                    </span>
                  )}
                </div>
                <p className="text-foreground font-body text-sm leading-relaxed mb-3">
                  {displayDescription}
                </p>
                {shouldTruncate && (
                  <button
                    onClick={() => toggleExpanded(index)}
                    className="text-primary hover:text-primary/80 text-sm font-body font-medium transition-gentle"
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
                {service?.features && service?.features?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex flex-wrap gap-1">
                      {service?.features?.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="px-2 py-1 bg-muted text-muted-foreground text-xs font-body rounded"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Add service card for premium users */}
          {canAddServices && (
            <div className="border-2 border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center text-center min-h-[200px] hover:border-primary/50 transition-gentle">
              <Icon name="Plus" size={32} className="text-muted-foreground mb-3" />
              <h3 className="font-heading font-medium text-foreground mb-2">
                Add Another Service
              </h3>
              <p className="text-sm text-muted-foreground font-body mb-4">
                {servicesLimit - servicesUsed} slots remaining
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddService}
                iconName="Plus"
                iconPosition="left"
                iconSize={14}
              >
                Add Service
              </Button>
            </div>
          )}

          {/* Upgrade card when at service limit */}
          {!canAddServices && servicesUsed >= servicesLimit && (
            <div className="border-2 border-dashed border-warning/30 rounded-lg p-4 flex flex-col items-center justify-center text-center min-h-[200px] hover:border-warning/50 transition-gentle">
              <Icon name="Crown" size={32} className="text-warning mb-3" />
              <h3 className="font-heading font-medium text-foreground mb-2">
                Service Limit Reached
              </h3>
              <p className="text-sm text-muted-foreground font-body mb-4">
                Unlock 4 more service slots for £4.99/year
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUpgradeClick('limit_reached')}
                iconName="Crown"
                iconPosition="left"
                iconSize={14}
              >
                Upgrade to Premium Plus
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatWeOfferSection;